"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import WKSDK, {
  ChannelTypePerson,
  ConnectStatus,
  MessageContentType,
  MessageText,
  type Message,
  type MessageText as MessageTextContent,
} from "wukongimjssdk";

const CUSTOMER_SERVICE_ID = "kf1";
const visitorStorageKey = "growdeep-im-visitor-id";
const imServerUrl = import.meta.env.VITE_IM_SERVER_URL_WS ?? "";
const imApiUrl = import.meta.env.VITE_IM_SERVER_URL ?? "";

type ApiMessage = {
  client_msg_no?: string;
  from_uid?: string;
  message_id?: number | string;
  message_idstr?: string;
  payload?: string;
  timestamp?: number;
};

export type ImCustomerChatMessage = {
  content: string;
  id: string;
  sender: "visitor" | "advisor";
  timestamp: Date;
};

function getVisitorId() {
  const storedId = window.localStorage.getItem(visitorStorageKey);
  if (storedId) return storedId;

  const visitorId = `web:${crypto.randomUUID()}`;
  window.localStorage.setItem(visitorStorageKey, visitorId);
  return visitorId;
}

function getMessageText(message: Message) {
  if (message.contentType === MessageContentType.image) return "[图片]";
  const content = message.content as MessageTextContent;
  return typeof content?.text === "string" ? content.text : "";
}

function decodePayload(payload?: string) {
  if (!payload) return "";

  try {
    const decoded = atob(payload);
    const bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
    const content = JSON.parse(new TextDecoder().decode(bytes)) as {
      content?: string;
      text?: string;
    };
    return content.content ?? content.text ?? "";
  } catch {
    return payload;
  }
}

function toChatMessage(message: ApiMessage, visitorId: string): ImCustomerChatMessage | null {
  const content = decodePayload(message.payload).trim();
  if (!content) return null;

  return {
    content,
    id: String(message.message_idstr ?? message.message_id ?? message.client_msg_no ?? Date.now()),
    sender: message.from_uid === visitorId ? "visitor" : "advisor",
    timestamp: new Date((message.timestamp ?? Date.now() / 1000) * 1000),
  };
}

export function useImCustomerChat(open: boolean) {
  const imRef = useRef<WKSDK | null>(null);
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState<ImCustomerChatMessage[]>([]);
  const [visitorId] = useState(() =>
    typeof window === "undefined" ? "" : getVisitorId(),
  );

  const loadHistory = useCallback(async () => {
    if (!visitorId || !imApiUrl) return;

    try {
      const response = await fetch(`${imApiUrl.replace(/\/+$/, "")}/channel/messagesync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel_id: CUSTOMER_SERVICE_ID,
          channel_type: ChannelTypePerson,
          end_message_seq: 0,
          limit: 30,
          login_uid: visitorId,
          pull_mode: 0,
          start_message_seq: 0,
        }),
      });
      if (!response.ok) return;

      const data = (await response.json()) as { messages?: ApiMessage[] };
      const history = (data.messages ?? [])
        .map((message) => toChatMessage(message, visitorId))
        .filter((message): message is ImCustomerChatMessage => message !== null)
        .sort((left, right) => left.timestamp.getTime() - right.timestamp.getTime());
      setMessages(history);
    } catch {
      setMessages([]);
    }
  }, [visitorId]);

  useEffect(() => {
    if (!open || !visitorId || !imServerUrl) return;

    const im = WKSDK.shared();
    im.config.addr = imServerUrl;
    im.config.debug = false;
    im.config.token = visitorId;
    im.config.uid = visitorId;
    im.config.provider.syncMessagesCallback = async () => [];
    imRef.current = im;

    const onStatusChange = (status: ConnectStatus) => {
      setConnected(status === ConnectStatus.Connected);
      if (status === ConnectStatus.Connected) void loadHistory();
    };
    const onMessage = (message: Message) => {
      if (message.fromUID !== CUSTOMER_SERVICE_ID) return;

      const content = getMessageText(message).trim();
      if (!content) return;

      setMessages((current) => [
        ...current,
        {
          content,
          id: message.messageID || message.clientMsgNo || String(Date.now()),
          sender: "advisor",
          timestamp: new Date((message.timestamp ?? Date.now() / 1000) * 1000),
        },
      ]);
    };

    im.connectManager.addConnectStatusListener(onStatusChange);
    im.chatManager.addMessageListener(onMessage);
    im.connect();

    return () => {
      im.connectManager.removeConnectStatusListener(onStatusChange);
      im.chatManager.removeMessageListener(onMessage);
      im.disconnect();
      imRef.current = null;
      setConnected(false);
      setMessages([]);
    };
  }, [open, visitorId, loadHistory]);

  const sendMessage = useCallback(
    async (value: string, pagePath: string) => {
      const content = value.trim();
      if (!content || !connected || !imRef.current || !visitorId) return false;

      try {
        const channel = imRef.current.newChannel(CUSTOMER_SERVICE_ID, ChannelTypePerson);
        const result = await imRef.current.chatManager.send(new MessageText(content), channel);
        setMessages((current) => [
          ...current,
          {
            content,
            id: result.messageID || result.clientMsgNo || String(Date.now()),
            sender: "visitor",
            timestamp: new Date(),
          },
        ]);
        void fetch("/api/im/notify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content, pagePath, visitorId }),
        });
        return true;
      } catch {
        return false;
      }
    },
    [connected, visitorId],
  );

  return { connected, messages, sendMessage };
}
